import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Mail, X } from "lucide-react";

const DISMISS_KEY = "bbk_opt_in_nudge_dismissed";
const DISMISS_DAYS = 30;

/**
 * Discreet mailing-list opt-in for /shop footer.
 * Shows only for logged-in buyers who aren't yet subscribed and haven't dismissed recently.
 */
export default function OptInNudge() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (!user) { setVisible(false); return; }
    if (user.role === "admin") { setVisible(false); return; }
    // Respect 30-day dismissal
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!Number.isNaN(ts) && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000) {
          setVisible(false);
          return;
        }
      }
    } catch {/* localStorage may be blocked */}
    // Check current subscription state
    (async () => {
      try {
        const { data } = await api.get("/subscription/me");
        if (!data.subscribed) setVisible(true);
      } catch {/* silent — nudge simply won't show */}
    })();
  }, [user]);

  if (!visible) return null;

  const subscribe = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/subscribe", { source: "catalog_nudge" });
      setSubscribed(true);
      toast.success("Subscribed — we'll send restocks first.");
      // Fade out after a beat
      setTimeout(() => setVisible(false), 2400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {/* fine */}
    setVisible(false);
  };

  return (
    <div
      className="mt-16 mb-4 border border-borderSubtle bg-white rounded-2xl overflow-hidden relative"
      data-testid="opt-in-nudge"
    >
      <div className="absolute inset-y-0 left-0 w-1 bg-oxblood" aria-hidden />
      <div className="grid md:grid-cols-[auto_1fr_auto] items-center gap-4 md:gap-6 px-5 md:px-7 py-5">
        <div className="hidden md:flex items-center justify-center w-11 h-11 rounded-full bg-oxblood/8 text-oxblood shrink-0">
          <Mail size={18} strokeWidth={1.75}/>
        </div>
        <div className="min-w-0">
          <p className="overline text-oxblood mb-1">Restock alerts</p>
          <p className="font-serif text-lg md:text-xl leading-tight text-charcoal mb-1">
            {subscribed ? "Welcome aboard." : "First to hear when kits land."}
          </p>
          <p className="text-xs md:text-sm text-stone leading-relaxed">
            {subscribed
              ? "You'll be the first to know when new batches hit. Manage anytime in Account."
              : "Occasional emails only — restocks, new kits, small sales. One-click unsubscribe in every email."}
          </p>
        </div>
        {!subscribed && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={subscribe}
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-charcoal hover:bg-oxblood text-bone tracking-[0.22em] text-xs uppercase rounded-full transition-colors disabled:opacity-50 whitespace-nowrap"
              data-testid="opt-in-subscribe-btn"
            >
              {busy ? "Adding…" : "Count me in"}
            </button>
            <button
              onClick={dismiss}
              className="text-stone hover:text-charcoal p-2 transition-colors rounded-full"
              aria-label="Dismiss opt-in prompt"
              title="Not now (hides for 30 days)"
              data-testid="opt-in-dismiss-btn"
            >
              <X size={16} strokeWidth={1.75}/>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
